import React from "react";
import {connect} from 'react-redux';
import {Link, Redirect} from "react-router-dom";

import {Button, Form} from "reactstrap";

import ExtMfaShell from "./ExtMfaShell";
import ExtMfaCodeInput from "./ExtMfaCodeInput";
import ExtMfaServerError, {SERVER_ERROR_FALLBACK} from "./ExtMfaServerError";
import {OutlineAlert} from "../components/ExtAlerts";

import {apiErrorCode, apiErrorMessage, isUnauthorised} from "../../../services/Api";
import {MFA_ERRORS, MFA_CODE_LENGTH} from "../../../config/MfaConfig";
import {
  mfaCleared,
  mfaUnenrolled,
  completeMfaLogin,
  confirmMfaChallenge,
  sendMfaEmailLink,
} from "../../../redux/actions/mfaActions";

// Every sign-in after the first: challenge the enrolled device, with an
// optional self-service email fallback.
//
// Named-exported undecorated for unit tests; the default export below is the
// connected version used by the route.
export class ExtMfaVerify extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      isSubmitting: false,
      isSendingEmail: false,
      code: '',
      error: '',
      serverError: '',
      emailSent: false,
      emailError: '',
      emailThrottled: false,
      throttleRetryAfter: null
    };

    this.isMounted_ = false;
  }

  componentDidMount() {
    this.isMounted_ = true;

    const pending = this.props.mfa.pending;

    if (!pending) {
      // No pending MFA session - abandoned, expired, or logged out.
      this.props.history.replace('/extranet/auth/signin');
      return;
    }

    if (!pending.mfa_enrolled) {
      // First sign-in belongs on the enrolment screen.
      this.props.history.replace('/extranet/auth/mfa/enrol');
      return;
    }
  }

  abandon = () => {
    this.props.clearMfa();
    this.props.history.replace('/extranet/auth/signin');
  }

  handleChange = (code) => {
    if (this.state.error) {
      this.setState({code: code, error: ''});
    } else {
      this.setState({code: code});
    }
  }

  canSubmit = () => {
    return (
      !this.state.isSubmitting &&
      this.state.code.length === MFA_CODE_LENGTH
    );
  }

  // The TOTP form shows until the user goes down the email path (fresh mail
  // sent or throttled resend) - afterwards only the email actions remain.
  // A 1028 replaces it entirely: there is nothing worth submitting.
  showCodeEntry = () => {
    return !this.state.serverError && !this.state.emailSent && !this.state.emailThrottled;
  }

  handleSubmit = (event) => {
    if (event) event.preventDefault();

    // Debounce: the server locks out repeated failures (contract section 7),
    // so guard against double submits client-side too.
    if (!this.canSubmit()) return;

    this.setState({isSubmitting: true, error: ''});

    confirmMfaChallenge(this.state.code)
      .then((data) => {
        this.props.completeMfaLogin(data);
        this.props.history.replace('/extranet/dashboard');
      })
      .catch((error) => {
        if (isUnauthorised(error)) {
          this.abandon();
          return;
        }

        if (apiErrorCode(error) === MFA_ERRORS.UNENROLLED) {
          // 1026 - server says unenrolled although the marker claims enrolled.
          // Correct the stale flag first, or the enrol screen bounces straight
          // back here on the same stale flag.
          this.props.markUnenrolled();
          this.props.history.replace('/extranet/auth/mfa/enrol');
          return;
        }

        if (apiErrorCode(error) === MFA_ERRORS.LOCKED_OUT) {
          // 1027 - too many failed attempts, locked out for a while. Stay and
          // show it; the form stays usable (attempts during lockout don't
          // extend it) so the user can retry once the window lapses.
          if (!this.isMounted_) return;
          this.setState({
            isSubmitting: false,
            code: '',
            error: apiErrorMessage(error, 'Too many incorrect attempts. Please try again in about 15 minutes.')
          });
          return;
        }

        if (apiErrorCode(error) === MFA_ERRORS.SECRET_UNREADABLE) {
          // 1028 - server-side fault, not retryable: replace the form and the
          // email actions rather than inviting another attempt.
          if (!this.isMounted_) return;
          this.setState({
            isSubmitting: false,
            code: '',
            serverError: apiErrorMessage(error, SERVER_ERROR_FALLBACK)
          });
          return;
        }

        if (!this.isMounted_) return;
        this.setState({
          isSubmitting: false,
          code: '',
          // 1022 - offer a retry plus the email fallback below.
          error: apiErrorMessage(error, 'That verification code failed. Please try again.')
        });
      });
  }

  handleSendEmail = () => {
    if (this.state.isSendingEmail) return;

    // Keep emailSent/emailThrottled during the request so a resend doesn't
    // flash the code form back while the new response is in flight.
    this.setState({isSendingEmail: true, emailError: ''});

    sendMfaEmailLink()
      .then((data) => {
        if (!this.isMounted_) return;
        if (data && data.throttled) {
          // Anti-spam throttle (contract section 5): nothing was mailed, so
          // say so explicitly instead of showing the fresh-mail success copy.
          const retryAfter = Number(data.retry_after_seconds);
          this.setState({
            isSendingEmail: false,
            emailSent: false,
            emailThrottled: true,
            throttleRetryAfter: isNaN(retryAfter) ? null : retryAfter
          });
          return;
        }
        this.setState({isSendingEmail: false, emailSent: true, emailThrottled: false});
      })
      .catch((error) => {
        if (isUnauthorised(error)) {
          this.abandon();
          return;
        }
        if (apiErrorCode(error) === MFA_ERRORS.SECRET_UNREADABLE) {
          // 1028 - server-side fault on the send itself.
          if (!this.isMounted_) return;
          this.setState({
            isSendingEmail: false,
            emailSent: false,
            emailThrottled: false,
            serverError: apiErrorMessage(error, SERVER_ERROR_FALLBACK)
          });
          return;
        }
        if (!this.isMounted_) return;
        this.setState({
          isSendingEmail: false,
          emailSent: false,
          emailThrottled: false,
          // 1024 - ask them to contact support.
          emailError: apiErrorMessage(error, 'We could not send the verification email. Please contact us for help.')
        });
      });
  }

  componentWillUnmount() {
    // Async callbacks below may settle after the screen is gone (navigation,
    // refresh); don't setState then.
    this.isMounted_ = false;
  }

  render() {
    if (this.props.user.currentUser.id) {
      return <Redirect to='/extranet/dashboard' />;
    }

    return (
      <ExtMfaShell
        title="Authentication"
        lead="Enter your verification code to sign in."
      >
        { !!this.state.serverError && (
          <ExtMfaServerError message={this.state.serverError} />
        ) }

        { !this.state.serverError && this.showCodeEntry() && (
          <p>Enter the generated code from your authenticator app in the field below and submit to verify and sign in.</p>
        ) }
        { !!this.state.error && (
          <OutlineAlert color="danger">
            {this.state.error}
          </OutlineAlert>
        ) }

        { this.state.emailSent && (
          <OutlineAlert color="success" dismissable={false}>
            <strong>Please check your email.</strong> The verification email has been sent to your registered email address.
            <br/>Note: the email expires in 10 minutes and can only be used once.
          </OutlineAlert>
        ) }

        { !!this.state.emailError && (
          <OutlineAlert color="danger">
            {this.state.emailError}
          </OutlineAlert>
        ) }

        { this.state.emailThrottled && (
          <OutlineAlert color="warning" dismissable={false}>
            Email already sent, check inbox/spam
            {typeof this.state.throttleRetryAfter === 'number'
              ? ` or wait ${this.state.throttleRetryAfter} seconds before trying again.`
              : ' before trying again.'}
          </OutlineAlert>
        ) }

        { this.showCodeEntry() && (
          <Form onSubmit={this.handleSubmit} className="form-login">
            <ExtMfaCodeInput
              id="mfa-verify-code"
              // label="Verification code"
              // hint="Codes change every 30 seconds - submit the current one promptly."
              value={this.state.code}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              disabled={this.state.isSubmitting}
            />
            <div className="text-center mt-3">
              <Button
                color="primary"
                size="lg"
                type="submit"
                disabled={!this.canSubmit()}
              >
                {this.state.isSubmitting ? 'Verifying...' : 'Verify and sign in'}
              </Button>
            </div>
          </Form>
        ) }

        { !this.state.serverError && (
        <div className="text-center mt-3">
          { (this.state.emailSent || this.state.emailThrottled) ? (
            <Button
              color="link"
              type="button"
              className="p-0"
              onClick={this.handleSendEmail}
              disabled={this.state.isSendingEmail}
            >
              {this.state.isSendingEmail ? 'Sending...' : 'Send the link again'}
            </Button>
          ) : (
            <Button
              color="link"
              type="button"
              className="p-0"
              onClick={this.handleSendEmail}
              disabled={this.state.isSendingEmail || this.state.isSubmitting}
            >
              {this.state.isSendingEmail ? 'Sending...' : 'Email verification link instead'}
            </Button>
          ) }
        </div>
        ) }

        <div className="text-center mt-3">
          <Link to="/extranet/auth/signin" onClick={this.props.clearMfa}>
            Cancel and start over
          </Link>
        </div>

        { !this.state.serverError && (
        <p className="text-center mt-3">
          <span className="text-muted">
            Having problems? Contact <a target="_blank" rel="noopener noreferrer" href="//www.cabfare.com.au/contact-us">CabFare</a> for assistance.
          </span>
        </p>
        ) }

      </ExtMfaShell>
    );
  } // render

} // ExtMfaVerify

const mapStateToProps = store => ({
  mfa: store.mfa,
  user: store.user
})

const mapDispatchToProps = dispatch => ({
  clearMfa: () => dispatch(mfaCleared()),
  markUnenrolled: () => dispatch(mfaUnenrolled()),
  completeMfaLogin: (payload) => completeMfaLogin(dispatch, payload)
})

export default connect(mapStateToProps, mapDispatchToProps)(ExtMfaVerify);